import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  DashboardItem,
  DashboardRowOne,
  DashboardWrapperStyled,
  RecentMarketplaceContainer,
} from './BuyerLoginDashboardComponent.styles';
import ScoreCard from 'components/ScoreCard/ScoreCard';
import DashboardItemList from 'components/DashboardItemList/DashboardItemList';
import Spinner from 'components/Spinner/Spinner';
import { Box } from '@mui/system';
import BuyerDashboardProductHistoryTable from 'components/BuyerDashboardProductHistoryTable/BuyerDashboardProductHistoryTable';
import { useQuery } from 'react-query';
import GraphSectionTitle from 'components/Typography/GraphSectionTitle/GraphSectionTitle';
import { useMediaQuery } from '@mui/material';
import GradientLineGraph from 'components/GradientLineGraph/GradientLineGraph';
import { getBuyerLoginDashboardData } from 'api';
import { useAuthContext } from 'context/AuthContext';
import { CURRENCY_MAP } from 'options/currency';

export const loginDashboardBorderString = '1px solid';

export const loginDashboardTabletBorders = {
  border: loginDashboardBorderString,
  borderRadius: 1.5,
  marginBottom: 3,
};

const BuyerLoginDashboardComponent = ({ mockData, isLoading, isStory }) => {
  const { currency } = useAuthContext();
  const currencyIcon = currency ? CURRENCY_MAP[currency] : '£';

  const dashboardFetch = useQuery(['buyerLoginDashboard'], () => getBuyerLoginDashboardData(), {
    enabled: !isStory,
    staleTime: 5 * 60 * 1000,
  });

  const data = useMemo(
    () => (isStory ? mockData : dashboardFetch?.data?.dashboard_data || {}),
    [mockData, isStory, dashboardFetch?.data?.dashboard_data]
  );

  const {
    last_views,
    last_five_products,
    total_products_bought,
    total_spending,
    active_direct_sales,
    active_briefs,
    transactions_thirty_days,
  } = data;

  const isTablet = useMediaQuery((theme) => theme.breakpoints.down(1200));

  const LOADING_MIN_HEIGHT = isTablet ? 100 : 200;

  const lastViews = useMemo(
    () =>
      last_views
        ? last_views.map((item) => ({ label: item.product_name, caption: item.date_viewed, uuid: item.product_uuid }))
        : [],
    [last_views]
  );

  const thirtyDaysGraphData = useMemo(() => {
    if (transactions_thirty_days && !isStory) {
      let labels = [];
      let data = [];
      Object.entries(transactions_thirty_days).forEach((item) => {
        labels.push(item[0]);
        data.push(item[1]);
      });
      return { labels, data, tooltipLabelText: 'Transaction' };
    } else return isStory ? transactions_thirty_days : {};
  }, [transactions_thirty_days, isStory]);

  const loadingStyles = !isTablet
    ? {
        borderTop: loginDashboardBorderString,
        borderRight: loginDashboardBorderString,
        minHeight: LOADING_MIN_HEIGHT,
      }
    : { ...loginDashboardTabletBorders, minHeight: LOADING_MIN_HEIGHT };

  return (
    <DashboardWrapperStyled isLoading={isLoading}>
      <DashboardRowOne>
        {(!dashboardFetch.data && !isStory) || (dashboardFetch.isLoading && !isStory) ? (
          <>
            <DashboardItem sx={loadingStyles} gridColumn={'1 / 2'} gridRow={'1 / 2 '}>
              <Box padding={'24px'}>
                <Spinner align={'center'} />
              </Box>
            </DashboardItem>
            <DashboardItem sx={loadingStyles} gridColumn={'2 / 3'} gridRow={'1 / 2 '}>
              <Box padding={'24px'}>
                <Spinner align={'center'} />
              </Box>
            </DashboardItem>
            <DashboardItem sx={loadingStyles} gridColumn={'1 / 2'} gridRow={'2 / 3 '}>
              <Box padding={'24px'}>
                <Spinner align={'center'} />
              </Box>
            </DashboardItem>
            <DashboardItem sx={loadingStyles} gridColumn={'2 / 3'} gridRow={'2 / 3 '}>
              <Box padding={'24px'}>
                <Spinner align={'center'} />
              </Box>
            </DashboardItem>
          </>
        ) : (
          <>
            <ScoreCard
              sx={
                !isTablet
                  ? { borderTop: loginDashboardBorderString, borderRight: loginDashboardBorderString, paddingLeft: 0 }
                  : loginDashboardTabletBorders
              }
              gridColumn={'1 / 2'}
              gridRow={'1'}
              title={'Total Completed'}
              value={total_products_bought}
            />
            <ScoreCard
              sx={
                !isTablet
                  ? { borderTop: loginDashboardBorderString, borderRight: loginDashboardBorderString }
                  : loginDashboardTabletBorders
              }
              gridColumn={'2 / 3'}
              gridRow={'1'}
              title={'Total Spent'}
              value={total_spending ? currencyIcon + total_spending.toLocaleString() : currencyIcon + '0.00'}
            />
            <ScoreCard
              sx={
                !isTablet
                  ? { borderTop: loginDashboardBorderString, borderRight: loginDashboardBorderString, paddingLeft: 0 }
                  : loginDashboardTabletBorders
              }
              gridColumn={'1 / 2'}
              gridRow={'2'}
              title={'Active Briefs'}
              value={active_briefs}
            />
            <ScoreCard
              sx={
                !isTablet
                  ? { borderTop: loginDashboardBorderString, borderRight: loginDashboardBorderString }
                  : loginDashboardTabletBorders
              }
              gridColumn={'2 / 3'}
              gridRow={'2'}
              title={'Active Purchases'}
              value={active_direct_sales}
            />
          </>
        )}
        <DashboardItem
          sx={!isTablet ? { borderTop: loginDashboardBorderString, paddingRight: 0 } : loginDashboardTabletBorders}
          showPadding
          gridColumn={'3 / 4'}
          gridRow={'1 / 3 '}
        >
          {(!dashboardFetch.data && !isStory) || (dashboardFetch.isLoading && !isStory) ? (
            <Box padding={'24px'}>
              <Spinner align={'center'} />
            </Box>
          ) : (
            <>
              <Box mb={2}>
                <GraphSectionTitle text={'Transactions in the last 30 days'} />
              </Box>
              <Box>
                <GradientLineGraph title={'Day'} {...thirtyDaysGraphData} />
              </Box>
            </>
          )}
        </DashboardItem>
        <DashboardItem
          sx={
            !isTablet
              ? { borderTop: loginDashboardBorderString, borderRight: loginDashboardBorderString, paddingLeft: 0 }
              : loginDashboardTabletBorders
          }
          showPadding
          showBorder
          gridColumn={'1 / 2'}
          gridRow={'3'}
        >
          {(!dashboardFetch.data && !isStory) || (dashboardFetch.isLoading && !isStory) ? (
            <Box padding={'24px'}>
              <Spinner align={'center'} />
            </Box>
          ) : (
            <>
              <Box mb={2}>
                <GraphSectionTitle text={'Recent marketplace product views'} variant="h4" />
              </Box>
              <RecentMarketplaceContainer>
                <DashboardItemList data={lastViews} />
              </RecentMarketplaceContainer>
            </>
          )}
        </DashboardItem>
        <DashboardItem
          sx={!isTablet ? { borderTop: loginDashboardBorderString, paddingRight: 0 } : loginDashboardTabletBorders}
          showPadding
          showBorder
          gridColumn={'2 / 4'}
          gridRow={'3'}
        >
          {(!dashboardFetch.data && !isStory) || (dashboardFetch.isLoading && !isStory) ? (
            <Box padding={'24px'}>
              <Spinner align={'center'} />
            </Box>
          ) : (
            <>
              <Box mb={2}>
                <GraphSectionTitle text={'Product history'} variant="h4" />
              </Box>
              <BuyerDashboardProductHistoryTable
                data={last_five_products || []}
                total={last_five_products?.length || 0}
              />
            </>
          )}
        </DashboardItem>
      </DashboardRowOne>
    </DashboardWrapperStyled>
  );
};

BuyerLoginDashboardComponent.propTypes = {};

export default BuyerLoginDashboardComponent;
